import { DesignDoc, Theme, forWire, textToHtml, withKeys } from '../designs/types';
import type { OfferBuckets } from '../VaryingNotice';

/**
 * The content shapes Campaigns, MEMAC and Saved templates share (backend:
 * Backend/email_library.py — library_* actions; memac.py — trigger content;
 * admin_email_targeting.parse_content — what a send reads).
 *
 * One e-mail is the words (EditableContent, in one of three formats) plus
 * who it is from (Envelope). A saved template, a past campaign, a design or
 * a trigger's own copy all arrive as SourceContent and become those two.
 */
export { forWire, withKeys };

/** text: plain words + up to 3 buttons (the built-in e-mails) · html: rich
 *  text (Campaigns "Write from scratch") · design: the block builder. */
export type BodyFormat = 'text' | 'html' | 'design';
export const FORMAT_LABEL: Record<BodyFormat, string> = { text: 'Plain text', html: 'Rich text', design: 'Design' };

export type Cta = { label: string; url: string };
export type Placeholder = { name: string; meaning: string };
export type Sender = { email: string; name: string };

/** Where a copy came from (content.source / library source). */
export type SourceRef = { kind: string; ref: string; name: string; version?: number; at: string };

/** What the shared editor edits. */
export type EditableContent = {
  format: BodyFormat;
  subject: string;
  /** text: the plain body (a blank line starts a paragraph); html: the HTML. Unused for design. */
  body: string;
  /** html: the optional plain-text part ('' = made from the HTML when sent). */
  text: string;
  /** design: the document being edited (client keys included). */
  design: DesignDoc | null;
  /** text: up to 3 buttons. */
  ctas: Cta[];
  /** design: the library design it started from (display only). */
  design_from: string;
  /** html: a whole HTML document (a pasted newsletter): open in HTML source. */
  raw_html?: boolean;
};
export const EMPTY_EDITABLE: EditableContent = {
  format: 'html', subject: '', body: '', text: '', design: null, ctas: [], design_from: '',
};

/** Who it is from. CC / BCC only where a campaign has them. */
export type Envelope = { from_email: string; from_name: string; reply_to: string; cc?: string; bcc?: string };
export const EMPTY_ENVELOPE: Envelope = { from_email: '', from_name: '', reply_to: '' };

// email_templates.FOUNDERS_INBOX / TEAM_INBOX: the MEMAC preset, and what a
// saved template gets when its From is left blank.
export const FOUNDERS_INBOX = 'inbox@universitytech.io';
export const TEAM_INBOX = 'info@universitytech.io';
/** A new e-mail's sender: Founders inbox@ (replies to info@) when it is an
 *  approved sender, else the first approved one. */
export const defaultEnvelope = (senders: Sender[]): Envelope => {
  const s = senders.find(x => x.email === FOUNDERS_INBOX) || senders[0];
  if (!s) return { ...EMPTY_ENVELOPE };
  return { from_email: s.email, from_name: s.name || '', reply_to: s.email === FOUNDERS_INBOX ? TEAM_INBOX : '' };
};

/** library_source: one shape for every "Start from…" kind (PLAN §9.1). */
export type SourceContent = {
  kind: 'library' | 'template' | 'campaign' | 'design' | 'trigger'; ref: string; name: string;
  format: BodyFormat; subject: string; body: string; text: string; design: DesignDoc | null;
  ctas: Cta[];
  from_email: string; from_name: string; reply_to: string; category: string;
  placeholder_fallbacks: Record<string, string>;
  /** Set when it can be used by reference (a catalogue or saved key, a template-mode campaign). */
  template_key?: string;
  raw_html?: boolean; warnings: string[];
  /** What the copy carries over that is worth knowing (a past campaign's own {upgrade_url} …). */
  notes?: string[];
  /** Campaigns only: MEMAC sent it. */
  from_memac?: boolean;
  source: SourceRef;
};

export type UsedBy = {
  triggers: { id: string; name: string; enabled: boolean; mode: string; content_mode?: string; uses?: boolean }[];
  campaigns: number;
  last_sent_at?: string | null;
};

/** A saved template (library_get / library_save). library_list rows have no body, text or design. */
export type LibraryRow = {
  key: string; name: string; description: string; category: string; format: BodyFormat;
  subject: string; body: string; text: string; design: DesignDoc | null; ctas: Cta[];
  from_email: string; from_name: string; reply_to: string;
  placeholders: { name: string; meaning: string; example: string }[];
  /** What each placeholder becomes for someone with no value; a campaign or trigger sending it may add its own over these. */
  placeholder_fallbacks?: Record<string, string>;
  origin: string; source: any; version: number; archived: boolean;
  archived_at?: string | null; archived_by?: string;
  created_by?: string; created_at?: string;
  updated_by: string; updated_at: string;
  used_by?: UsedBy;
};

/** memac_triggers.templates item (memac._template_options). */
export type TriggerTemplateOption = {
  key: string; name: string; group: string; category: string | null;
  library: boolean; format: BodyFormat; version: number | null; primary_cta: string; send_html: boolean;
  primary_cta_url?: string;
};

/** library_list meta. */
export type LibraryMeta = {
  templates: LibraryRow[];
  group: string;
  categories: { key: string; label: string }[];
  formats: { key: BodyFormat; label: string }[];
  limits: { templates: number; active: number; versions: number; name: number; description: number;
    subject: number; body: number; ctas: number };
  placeholders: Placeholder[];
  senders: Sender[];
};

/** library_render / memac_preview. rendered_for is an object (library) or a string (MEMAC). */
export type Rendered = {
  subject: string; html: string; text: string; from_email: string; from_name: string; reply_to: string;
  ctas: Cta[]; send_html: boolean; rendered_for?: { email?: string; name?: string } | string | null;
  footer_added?: boolean; unsubscribe_ok?: boolean; unknown?: string[]; blank?: string[]; warnings?: string[];
  /** Placeholders that differ per person: who would see which value (VaryingNotice). */
  varying?: Record<string, OfferBuckets>;
};
export const renderedFor = (r: Rendered | null | undefined): string => {
  const v = r?.rendered_for;
  if (!v) return 'sample values';
  if (typeof v === 'string') return v;
  return v.email || 'sample values';
};

// ---------------------------------------------------------------------------
// Conversions between the wire and the editor
// ---------------------------------------------------------------------------
const cleanCtas = (ctas: Cta[] | null | undefined): Cta[] =>
  (ctas || []).map(c => ({ label: String(c?.label ?? ''), url: String(c?.url ?? '') }));

/** SourceContent -> what the editors hold. */
export const fromSource = (src: SourceContent): {
  content: EditableContent; envelope: Envelope; fallbacks: Record<string, string>;
  source: SourceRef; category: string; warnings: string[];
} => ({
  content: {
    format: src.format,
    subject: src.subject || '',
    body: src.body || '',
    text: src.text || '',
    design: src.design ? withKeys(src.design) : null,
    ctas: src.format === 'text' ? cleanCtas(src.ctas) : [],
    design_from: src.format === 'design' ? src.name : '',
    raw_html: !!src.raw_html,
  },
  envelope: { from_email: src.from_email || '', from_name: src.from_name || '', reply_to: src.reply_to || '' },
  fallbacks: { ...(src.placeholder_fallbacks || {}) },
  source: { ...src.source, kind: src.source?.kind || src.kind, ref: src.source?.ref || src.ref,
    name: src.source?.name || src.name, at: src.source?.at || new Date().toISOString() },
  category: src.category || '',
  warnings: src.warnings || [],
});

/** A saved template (full row) -> what the editors hold. */
export const fromLibraryRow = (t: LibraryRow): {
  content: EditableContent; envelope: Envelope; fallbacks: Record<string, string>;
} => ({
  content: {
    format: t.format, subject: t.subject || '', body: t.body || '', text: t.text || '',
    design: t.design ? withKeys(t.design) : null, ctas: t.format === 'text' ? cleanCtas(t.ctas) : [],
    design_from: '', raw_html: t.format === 'html' && /<html[\s>]/i.test(t.body || ''),
  },
  envelope: { from_email: t.from_email || '', from_name: t.from_name || '', reply_to: t.reply_to || '' },
  fallbacks: Object.fromEntries(Object.entries(t.placeholder_fallbacks || {}).map(([k, v]) => [k, String(v ?? '')])),
});

/** The content fields as library_save / library_render (draft) read them. */
export const contentFields = (c: EditableContent): Record<string, any> => ({
  body_format: c.format,
  subject: c.subject,
  body: c.format === 'design' ? '' : c.body,
  text: c.format === 'html' ? c.text : '',
  design: c.format === 'design' && c.design ? forWire(c.design) : null,
  ctas: c.format === 'text' ? c.ctas.filter(x => x.label.trim() || x.url.trim()) : [],
});

/** POST email/library_save (and the draft of library_render / library_test_send). */
export const toLibraryPayload = (c: EditableContent, env: Envelope, meta: {
  key?: string; name: string; description?: string; category: string;
  origin?: 'library' | 'campaigns' | 'memac'; source?: SourceRef | null;
  /** What each placeholder becomes for someone with no value ({} = the defaults). */
  fallbacks?: Record<string, string>;
  expected_version?: number; confirm_live?: boolean;
}): Record<string, any> => {
  const out: Record<string, any> = {
    ...contentFields(c),
    name: meta.name.trim(),
    description: (meta.description || '').trim(),
    category: meta.category || '',
    from_email: env.from_email,
    from_name: env.from_name.trim(),
    reply_to: env.reply_to.trim(),
    placeholder_fallbacks: { ...(meta.fallbacks || {}) },
  };
  if (meta.key) {
    out.key = meta.key;
    out.expected_version = meta.expected_version;
    if (meta.confirm_live) out.confirm_live = true;
  } else {
    out.origin = meta.origin || 'library';
    if (meta.source) out.source = meta.source;
  }
  return out;
};

/** A trigger's own copy (memac_trigger_save content). MEMAC sends html or
 *  design; a plain-text copy goes as rich text (its buttons become links). */
export const toTriggerContent = (c: EditableContent, env: Envelope, extras: {
  source?: SourceRef | null; fallbacks?: Record<string, string>; name?: string;
} = {}): { content_mode: 'custom' | 'design'; content: Record<string, any> } => {
  const cc = c.format === 'text' ? convertContent(c, 'html').content : c;
  const content: Record<string, any> = cc.format === 'design'
    ? { subject: cc.subject, design: cc.design ? forWire(cc.design) : null, design_from: cc.design_from || '' }
    : { subject: cc.subject, body: cc.body, text: cc.text };
  content.from_email = env.from_email;
  content.from_name = env.from_name.trim();
  content.reply_to = env.reply_to.trim();
  if (extras.name) content.name = extras.name;
  if (extras.source) content.source = extras.source;
  if (extras.fallbacks && Object.keys(extras.fallbacks).length) content.placeholder_fallbacks = extras.fallbacks;
  return { content_mode: cc.format === 'design' ? 'design' : 'custom', content };
};

/** What would stop a save or a preview, in words ('' when none). */
export const contentProblem = (c: EditableContent): string => {
  if (!c.subject.trim()) return 'Write a subject.';
  if (c.format === 'design') return c.design?.blocks?.length ? '' : 'Pick or build a design.';
  if (!c.body.replace(/<[^>]*>/g, '').trim()) return 'Write the e-mail body.';
  return '';
};

// ---------------------------------------------------------------------------
// Format switch in the editor (an unsaved draft, so done here; lossy, and
// the editor asks first). Saved sources convert on the server instead
// (library_source {as}).
// ---------------------------------------------------------------------------
const PLAIN_THEME: Theme = {
  accent: '#1A73E8', accent_text: '#FFFFFF', bg: '#FFFFFF', card: '#FFFFFF', text: '#202124',
  muted: '#5F6368', font: 'helvetica', radius: 6,
};
const escHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const escAttr = (s: string) => escHtml(s).replace(/"/g, '&quot;');

/** HTML -> plain text with blank-line paragraphs (links as "words (url)"). */
export const htmlToPlain = (html: string): string => {
  if (!html.trim()) return '';
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const out: string[] = [];
  const walk = (n: Node): string => {
    if (n.nodeType === Node.TEXT_NODE) return (n.textContent || '').replace(/\s+/g, ' ');
    if (n.nodeType !== Node.ELEMENT_NODE) return '';
    const el = n as HTMLElement;
    if (/^(SCRIPT|STYLE|HEAD|TITLE)$/.test(el.tagName)) return '';
    if (el.tagName === 'BR') return '\n';
    const inner = Array.from(el.childNodes).map(walk).join('');
    if (el.tagName === 'A') {
      const href = (el.getAttribute('href') || '').trim();
      return href && href !== inner.trim() ? `${inner} (${href})` : inner;
    }
    if (el.tagName === 'LI') return `\n• ${inner.trim()}`;
    if (/^(P|DIV|H[1-6]|BLOCKQUOTE|UL|OL|TABLE|TR|SECTION|HEADER|FOOTER|CENTER)$/.test(el.tagName)) return `\n\n${inner}\n\n`;
    if (el.tagName === 'TD') return `${inner} `;
    return inner;
  };
  out.push(walk(doc.body.firstElementChild || doc.body));
  return out.join('').split('\n').map(l => l.replace(/[ \t]+/g, ' ').trim()).join('\n')
    .replace(/\n{3,}/g, '\n\n').trim();
};

/** Buttons as linked paragraphs (rich text has no buttons). */
const ctaLinks = (ctas: Cta[]) => ctas.filter(x => x.label.trim() && x.url.trim())
  .map(x => `<p><a href="${escAttr(x.url.trim())}">${escHtml(x.label.trim())}</a></p>`).join('');

/** The text of a design, block by block, as rich text. */
const designHtml = (d: DesignDoc | null): { html: string; ctas: Cta[] } => {
  const parts: string[] = [];
  const ctas: Cta[] = [];
  (d?.blocks || []).forEach(b => {
    if (b.type === 'heading' && b.text) parts.push(`<p><b>${escHtml(b.text)}</b></p>`);
    else if ((b.type === 'text' || b.type === 'callout' || b.type === 'footer') && b.html) parts.push(b.html);
    else if (b.type === 'buttons') (b.buttons || []).forEach(x => ctas.push({ label: x.label, url: x.url }));
    else if (b.type === 'image' && b.href) parts.push(`<p><a href="${escAttr(b.href)}">${escHtml(b.alt || b.href)}</a></p>`);
  });
  return { html: parts.join(''), ctas };
};

export const convertContent = (c: EditableContent, to: BodyFormat, theme?: Theme): {
  content: EditableContent; warnings: string[];
} => {
  if (c.format === to) return { content: c, warnings: [] };
  const warnings: string[] = [];
  const base: EditableContent = { ...c, format: to, raw_html: false };
  if (to === 'html') {
    if (c.format === 'text') {
      const links = ctaLinks(c.ctas);
      if (links) warnings.push('The buttons became links; pick Design to keep buttons.');
      return { content: { ...base, body: textToHtml(c.body) + links, text: '', ctas: [], design: null }, warnings };
    }
    const d = designHtml(c.design);
    warnings.push('Only the words and links are kept; the layout and styling are dropped.');
    return { content: { ...base, body: d.html + ctaLinks(d.ctas), text: '', ctas: [], design: null, design_from: '' }, warnings };
  }
  if (to === 'text') {
    if (c.format === 'html') {
      warnings.push('Formatting is dropped; links are kept as their address.');
      return { content: { ...base, body: htmlToPlain(c.body), text: '', ctas: [], design: null }, warnings };
    }
    const d = designHtml(c.design);
    warnings.push('Only the words and up to 3 buttons are kept; the layout and styling are dropped.');
    return { content: { ...base, body: htmlToPlain(d.html), ctas: d.ctas.slice(0, 3), text: '', design: null, design_from: '' }, warnings };
  }
  // -> design: the words in a text block, the buttons in a buttons block.
  const th = { ...(theme || PLAIN_THEME) };
  const words = c.format === 'text' ? textToHtml(c.body) : c.body;
  const ctas = c.format === 'text' ? c.ctas.filter(x => x.label.trim() && x.url.trim()).slice(0, 3) : [];
  const blocks: any[] = [{ type: 'text', align: 'left', html: words || '<p></p>' }];
  if (ctas.length) {
    blocks.push({ type: 'buttons', align: 'left', buttons: ctas.map((x, i) => ({
      label: x.label, url: x.url, style: i === 0 ? 'solid' : 'outline', bg: th.accent, color: th.accent_text })) });
  }
  if (c.format === 'html' && /<html[\s>]|<table[\s>]|style=/i.test(c.body)) {
    warnings.push('A design keeps simple formatting only (bold, italic, links, lists); styled HTML is simplified.');
  }
  return { content: { ...base, body: '', text: '', ctas: [], design: withKeys({ theme: th, blocks }), design_from: '' }, warnings };
};
